import { __awaiter } from "tslib";
import { ConstraintSystem, Constraint } from 'barretenberg-es/standard_composer';
import { formatOptionUsage } from './terminal_buffer';
const commandOptions = {
    a: {
        longName: 'alias',
        description: 'Give alias to the system.',
        valueName: 'name',
        valueRequired: true,
    },
    i: {
        longName: 'interactive',
        description: 'Set arguments interactively.',
    },
};
const logUsage = (buf, msg = '') => __awaiter(void 0, void 0, void 0, function* () {
    let texts = [];
    if (msg) {
        if (typeof msg === 'string') {
            texts.push({ text: msg });
        }
        else {
            texts = texts.concat(msg);
        }
    }
    texts = texts.concat([
        { text: 'usage: create [<constraintSystem>] [<option>]' },
        { text: '' },
        { text: 'Constraint System:' },
        { text: '  <varnum>,<pubvarnum>,<constraint1>[,...<constraintN>]' },
        { text: '  · varnum                  Number of variables.' },
        { text: '  · pubvarnum               Number of public variables.' },
        { text: '  · constraint              {<a>,<b>,<c>,<ql>,<qr>,<qm>,<qo>,<qc>}' },
        { text: '' },
        { text: 'Options:' },
        ...Object.keys(commandOptions)
            .map(name => formatOptionUsage(name, commandOptions[name]))
            .map(text => ({ text })),
        { text: '' },
        {
            text: [
                { text: 'For example, the command for the system with a single constraint ' },
                { text: 'x + y = z', color: 'magenta', bold: true },
                { text: ' will be:' },
            ],
            color: 'grey',
        },
        {
            text: [{ text: '`' }, { text: 'create 3,0,{1,2,3,0,1,1,-1,0}', color: 'grey', bold: true }, { text: '`' }],
        },
        { text: '' },
        {
            text: [
                { text: 'And for the system with 2 constraints ' },
                { text: 'x + y = z', color: 'magenta', bold: true },
                { text: ' and ' },
                { text: 'y * z + 1 = w', color: 'magenta', bold: true },
                { text: ':' },
            ],
            color: 'grey',
        },
        {
            text: [
                { text: '`' },
                { text: 'create 4,0,{1,2,3,0,1,1,-1,0},{2,3,4,1,0,0,-1,1}', color: 'grey', bold: true },
                { text: '`' },
            ],
        },
        { text: '' },
        {
            text: [
                { text: 'Or enter `' },
                { text: 'create -i', bold: true },
                { text: '` and follow the instructions on screen.' },
            ],
            color: 'grey',
        },
        { text: '' },
    ]);
    yield buf.log(texts);
});
const getConstraints = (cmdStr) => {
    const constraints = cmdStr.match(/{(-?\d+,){7}-?\d+}/g);
    if (!constraints || !constraints.length) {
        return null;
    }
    return constraints.map(constraint => {
        const [a, b, c, ql, qr, qm, qo, qc] = constraint
            .slice(1, -1)
            .split(',')
            .map(v => +v);
        return new Constraint(a, b, c, ql, qr, qm, qo, qc);
    });
};
const getConstraintSystem = (argv) => {
    const cmdStr = argv.filter((v, i) => i > 0 && !v.startsWith('-') && !argv[i - 1].startsWith('-')).join('');
    const [_, varnum, pubvarnum] = cmdStr.match(/(\d+),(\d+),{/) || [];
    if (!varnum) {
        return null;
    }
    const constraints = getConstraints(cmdStr);
    if (!constraints) {
        return null;
    }
    return new ConstraintSystem(+varnum, +pubvarnum, constraints);
};
const getConstraintSystemInteractively = (buf) => __awaiter(void 0, void 0, void 0, function* () {
    let varnum;
    const defaultVarnum = 3;
    while (varnum === undefined) {
        varnum = (yield buf.prompt(`Number of variables (${defaultVarnum})`)).trim();
        if (!varnum) {
            varnum = defaultVarnum;
            yield buf.replace(`${defaultVarnum}`);
        }
        else if (!varnum.match(/^[1-9][0-9]*$/)) {
            varnum = undefined;
            yield buf.log([{ text: 'Invalid number of variables.', color: 'red' }]);
        }
        else {
            varnum = +varnum;
        }
    }
    let pubvarnum;
    const defaultPubvarnum = 0;
    while (pubvarnum === undefined) {
        pubvarnum = (yield buf.prompt(`Number of public variables (${defaultPubvarnum})`)).trim();
        if (!pubvarnum) {
            pubvarnum = defaultPubvarnum;
            yield buf.replace(`${defaultPubvarnum}`);
        }
        else if (!pubvarnum.match(/^[0-9]+$/)) {
            pubvarnum = undefined;
            yield buf.log([{ text: 'Invalid number of public variables.', color: 'red' }]);
        }
        else {
            pubvarnum = +pubvarnum;
            if (pubvarnum > varnum) {
                pubvarnum = undefined;
                yield buf.log([
                    { text: "Number of public variables can't be more than the number of variables.", color: 'red' },
                ]);
            }
        }
    }
    let numberOfContraints;
    const defaultNumberOfConstraints = 1;
    while (numberOfContraints === undefined) {
        numberOfContraints = (yield buf.prompt(`Number of constraints (${defaultNumberOfConstraints})`)).trim();
        if (!numberOfContraints) {
            numberOfContraints = defaultNumberOfConstraints;
            yield buf.replace(`${defaultNumberOfConstraints}`);
        }
        else if (!numberOfContraints.match(/^[1-9][0-9]*$/)) {
            numberOfContraints = undefined;
            yield buf.log([{ text: 'Invalid number of constraints.', color: 'red' }]);
        }
        else {
            numberOfContraints = +numberOfContraints;
        }
    }
    const constraints = [];
    for (let i = 0; i < numberOfContraints; ++i) {
        while (constraints.length < i + 1) {
            const csStr = (yield buf.prompt([
                {
                    text: [{ text: `Constraint ${i + 1} ` }, { text: `{a,b,c,ql,qr,qm,qo,qc}`, color: 'grey' }],
                },
            ])).trim();
            if (!csStr) {
                yield buf.log([{ text: 'Please define the constraint.', color: 'red' }]);
            }
            else {
                const parsed = getConstraints(`{${csStr.replace(/\s/g, '')}}`);
                if (parsed) {
                    constraints.push(parsed[0]);
                }
                else {
                    yield buf.log([{ text: 'Invalid constraint.', color: 'red' }]);
                }
            }
        }
    }
    return new ConstraintSystem(varnum, pubvarnum, constraints);
});
const run = (buf, app) => __awaiter(void 0, void 0, void 0, function* () {
    const { argv, options } = buf;
    let constraintSystem;
    let aliasName = options.a || '';
    if (options.i) {
        constraintSystem = yield getConstraintSystemInteractively(buf);
    }
    else {
        const [, cs] = argv;
        if (!cs) {
            yield logUsage(buf, [{ text: 'Please provide a constraint system.', color: 'red' }]);
            return;
        }
        constraintSystem = getConstraintSystem(argv);
        if (!constraintSystem) {
            yield buf.log([{ text: 'Invalid constraint system.' }]);
            return;
        }
        if (constraintSystem.pubvarnum > constraintSystem.varnum) {
            yield buf.log([{ text: "Number of public variables can't be more than the number of variables.", color: 'red' }]);
            return;
        }
    }
    if (!aliasName && options.i) {
        aliasName = yield buf.prompt('Alias of the system');
    }
    if (aliasName) {
        const existingCS = yield app.getComposerWithAlias(aliasName);
        if (existingCS) {
            buf.log([
                {
                    text: `Alias '${aliasName}' already exists.`,
                    color: 'red',
                },
            ]);
            return;
        }
    }
    const dbCS = yield app.addComposer(constraintSystem, aliasName);
    buf.log([
        {
            text: `System created! Alias: ${dbCS.alias}`,
            color: 'green',
        },
    ]);
});
export const create = {
    run,
    logUsage,
    commandOptions,
};
//# sourceMappingURL=data:application/json;base64,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